import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import moment from 'moment';

import {
  Error403Unauthorized,
  FastCreationModal,
  CustomAlert,
  SetBranchesComponent,
  Phone,
} from '@app/components';
import { SelectField, SnilsInput } from '@app/components/FormFields';
import { DictionarySelect } from '@app/components/RecordModal/components/MedicalCard/components';
import { MedicalDictionaryType } from '@app/components/RecordModal/components/MedicalCard/data';
import { signerRoleOptions } from '@app/components/RecordModal/components/MedicalCard/data/dictionaries';
import {
  PREFIX,
  GENDER,
  ERROR_REQUIRED_FIELD,
  ADD_POSITION_LABEL,
  BRANCHES_LABEL,
  DATE_PICKER_FORMAT,
  OFF,
  ONE_TIME_CODE,
  rangePickerLocale,
  EMAIL_TYPE_VALIDATION,
  EMAIL_LENGTH_VALIDATION,
  PASSWORD_VALIDATION_RULES,
} from '@app/constants';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import { useAddEmployee } from '@pages/Employees/AddEmployee/hooks/useAddEmployee';

import type { FC } from 'react';

import '@pages/Employees/AddEmployee/styles/AddEmployee.desktop.scss';
import '@pages/Employees/AddEmployee/styles/AddEmployee.tablet.scss';

export const AddEmployee: FC = () => {
  const {
    isFetchingLoading,
    form,
    onFinish,
    positions,
    error,
    errorType,
    setError,
    open,
    selectedBranches,
    setSelectedBranches,
    showUserModal,
    hideUserModal,
    allBranches,
    contextHolder,
    addPosition,
    handleOk,
    handleCancel,
    isModalOpen,
    modalForm,
    finishModal,
    handleCancelSaving,
    setNewData,
    hasUserRightToCreateUser,
    hasUserRightToCreatePosition,
    hasUserRightToChangeBranches,
    setInputData,
    setSelectorData,
    setDatePickerData,
    initialValues,
    isSavingLoading,
    setPhoneValidation,
  } = useAddEmployee();

  if (!hasUserRightToCreateUser) {
    return <Error403Unauthorized />;
  }

  if (isFetchingLoading) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}add-employee bc-form`}>
      {contextHolder}
      <Form
        className="add-employee-form"
        form={form}
        onFinish={onFinish}
        autoComplete={OFF}
        initialValues={initialValues}
      >
        <Space direction="vertical" size="large" className="form-space">
          <div className="container">
            <Typography.Text className="label" strong data-testid="surname">
              Фамилия
            </Typography.Text>

            <Form.Item name="NameLast" className="mg-0">
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={60}
                data-testid="surname-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'NameLast',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text
              className="label-require label"
              strong
              data-testid="name"
            >
              Имя
            </Typography.Text>

            <Form.Item
              name="NameFirst"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
              ]}
              className="mg-0"
            >
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={60}
                data-testid="name-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'NameFirst',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text className="label" strong data-testid="patronymic">
              Отчество
            </Typography.Text>

            <Form.Item name="NameMiddle" className="mg-0">
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={60}
                data-testid="patronymic-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'NameMiddle',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text
              strong
              className="label label-require"
              data-testid="email"
            >
              Email
            </Typography.Text>
            <Form.Item
              name="Email"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
                EMAIL_TYPE_VALIDATION,
                EMAIL_LENGTH_VALIDATION,
              ]}
              className="mg-0 second-column"
            >
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                data-testid="email-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Email',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
                maxLength={100}
              />
            </Form.Item>
          </div>

          <SnilsInput fieldName="Snils" label="СНИЛС врача (11 цифр)" />

          <SelectField
            label="Медицинская должность"
            fieldName={'SignerRole'}
            placeholder="Выберите должность"
            options={signerRoleOptions}
            size="large"
          />

          <DictionarySelect
            type={MedicalDictionaryType.Positions}
            title="Код должности врача по НСИ"
            name={'PositionCode'}
          />

          <DictionarySelect
            type={MedicalDictionaryType.Specialities}
            title="Код специальности из НСИ"
            name={'SpecialityCodeNsi'}
          />

          <div className="container">
            <Typography.Text
              strong
              className="label"
              data-testid="new-password"
            >
              Пароль
            </Typography.Text>
            <Form.Item
              name="Password"
              rules={[...PASSWORD_VALIDATION_RULES]}
              className="mg-0 second-column"
            >
              <Input.Password
                autoComplete={ONE_TIME_CODE}
                size="large"
                data-testid="new-password-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Password',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
                maxLength={100}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text
              strong
              className="label label-require"
              data-testid="phone"
            >
              Мобильный телефон
            </Typography.Text>
            <Form.Item name="Phone" className="mg-0 second-column">
              <Phone
                name="Phone"
                required
                onValidate={setPhoneValidation}
                disabled={isSavingLoading}
                phoneInputDataTestId="phone-input"
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text className="label" strong data-testid="telegram">
              Telegram
            </Typography.Text>
            <Form.Item name="TelegramId" className="mg-0 second-column">
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={100}
                data-testid="telegram-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'TelegramId',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text className="label" strong data-testid="position">
              Должность
            </Typography.Text>

            <Form.Item name="Position" className="mg-0">
              <Select
                size="large"
                showSearch
                optionFilterProp="label"
                data-testid="position-select"
                options={positions.map((position) => {
                  return {
                    label: position.Name,
                    value: position.Id,
                  };
                })}
                popupRender={(menu) => (
                  <>
                    {hasUserRightToCreatePosition ? (
                      <>
                        <Button type="link" size="small" onClick={addPosition}>
                          {ADD_POSITION_LABEL}
                        </Button>
                        <Divider className="position-divider" />
                        {menu}
                      </>
                    ) : (
                      <>{menu}</>
                    )}
                  </>
                )}
                onChange={(value) => {
                  setSelectorData({ value, fieldName: 'Position' });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text className="label" strong data-testid="gender">
              Пол
            </Typography.Text>

            <Form.Item name="Gender" className="mg-0">
              <Select
                size="large"
                options={GENDER}
                data-testid="gender-select"
                onChange={(value) => {
                  setSelectorData({ value, fieldName: 'Gender' });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text className="label" strong data-testid="birth-date">
              Дата рождения
            </Typography.Text>
            <Form.Item name="DateBirth" className="mg-0">
              <DatePicker
                autoComplete={ONE_TIME_CODE}
                locale={rangePickerLocale}
                format={DATE_PICKER_FORMAT}
                size="large"
                data-testid="birth-date-select"
                onChange={(value) => {
                  setDatePickerData({
                    dateString: value,
                    fieldName: 'Date',
                  });
                }}
                disabledDate={(current) =>
                  current.unix() >= moment().startOf('day').unix()
                }
              />
            </Form.Item>
          </div>
          <div className="container description-container">
            <Typography.Text className="label" strong data-testid="description">
              Описание
            </Typography.Text>

            <Form.Item name="Description" className="mg-0">
              <Input.TextArea
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={260}
                data-testid="description-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Description',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container description-container">
            <Typography.Text className="label" strong>
              Примечание
            </Typography.Text>

            <Form.Item name="Note" className="mg-0">
              <Input.TextArea
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={260}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Note',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <Divider className="divider" />
          <div
            className={`
              branches-container
              ${
                !hasUserRightToChangeBranches
                  ? ' has-no-right-to-change-branch'
                  : ''
              }
            `}
          >
            <Typography.Title level={3} className="mg-0" data-testid="branches">
              {BRANCHES_LABEL}
            </Typography.Title>
            <Typography.Text type="secondary" data-testid="branches-hint">
              Укажите, в каких филиалах может работать исполнитель
            </Typography.Text>
            <SetBranchesComponent
              open={open}
              onCancel={hideUserModal}
              selectedBranches={selectedBranches}
              setSelectedBranches={setSelectedBranches}
              allBranches={allBranches}
              showUserModal={showUserModal}
              tooltipLabel="Укажите, в каких филиалах может работать исполнитель"
              newDataFieldValue="selectedBranches"
              setNewData={setNewData}
            />
          </div>
          {error && errorType && (
            <CustomAlert
              errorType={errorType}
              serverError={error}
              setServerError={setError}
            />
          )}
          <div className="action-buttons">
            <Button
              type="primary"
              htmlType="submit"
              className="save-button"
              loading={isSavingLoading}
              size="large"
              data-testid="save-edit-employee"
            >
              Сохранить
            </Button>

            <Button
              className="save-button"
              disabled={isSavingLoading}
              size="large"
              onClick={handleCancelSaving}
              data-testid="cancel-add-employee"
            >
              Отменить
            </Button>
          </div>
        </Space>
      </Form>
      <FastCreationModal
        title="Создание должности"
        isModalOpen={isModalOpen}
        handleOk={handleOk}
        handleCancel={handleCancel}
        modalForm={modalForm}
        finishModal={finishModal}
        nameMaxLength={40}
        descriptionMaxLength={260}
      />
    </div>
  );
};
